import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Gift, Plus, Loader2, ChevronRight, AlertTriangle } from 'lucide-react';
import {
  adminCompetitions,
  adminCreatePartnerCodeBatch,
  adminListPartnerCodeBatches,
} from '@/lib/api';

/**
 * Partner Code Batches — list view + "New batch" generator.
 *
 * This replaces the old single-toggle Partner Code page. Every batch here
 * creates N sequential single-use codes (BASEWORD1..BASEWORDN) backed by
 * the atomic server-side single-use pipeline. The per-batch detail page
 * (`/admin/partner-codes/:id`) handles the email composer, assignment,
 * live redemption table, and the "void remaining" button.
 */
const AdminPartnerCodes = () => {
  const [batches, setBatches] = useState([]);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ base_word: '', label: '', quantity: 50, competition_id: '' });
  // Confirmation state when a collision was detected on first submit.
  const [collision, setCollision] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListPartnerCodeBatches();
      setBatches(res.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load batches.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    adminCompetitions().then((r) => setComps(r.items || [])).catch(() => setComps([]));
  }, [refresh]);

  const liveComps = useMemo(
    () => comps.filter((c) => ['LIVE', 'COMING_SOON'].includes((c.status || '').toUpperCase())),
    [comps],
  );
  useEffect(() => {
    if (!form.competition_id && liveComps.length > 0) {
      setForm((f) => ({ ...f, competition_id: liveComps[0].id }));
    }
  }, [liveComps, form.competition_id]);

  const submit = async (e, { force = false, start_at } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    const base = (form.base_word || '').trim();
    const label = (form.label || '').trim();
    const qty = Number(form.quantity);
    if (!base || !label || !form.competition_id || !Number.isFinite(qty) || qty < 1) {
      toast.error('Fill in every field. Quantity must be at least 1.');
      return;
    }
    setCreating(true);
    try {
      const row = await adminCreatePartnerCodeBatch({
        base_word: base,
        label,
        competition_id: form.competition_id,
        quantity: qty,
        ...(start_at ? { start_at } : {}),
        force,
      });
      setBatches((prev) => [row, ...prev]);
      setForm({ base_word: '', label: '', quantity: 50, competition_id: form.competition_id });
      setCollision(null);
      toast.success(`Generated ${row.quantity} code${row.quantity === 1 ? '' : 's'} for ${row.label}.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d && typeof d === 'object' && d.collision) {
        setCollision({
          message: d.message,
          existing_count: d.existing_count,
          example_existing: d.example_existing,
          suggested_start_at: d.suggested_start_at,
        });
      } else {
        toast.error(typeof d === 'string' ? d : 'Could not create batch.');
      }
    } finally { setCreating(false); }
  };

  return (
    <div data-testid="admin-partner-codes-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-widest">Partnerships</div>
        <h1 className="font-display text-3xl md:text-4xl leading-tight mt-1">Partner Code Batches</h1>
        <p className="mt-2 text-sm text-muted-sage max-w-2xl">
          Generate sequential single-use codes for an event or partner, email them out via Resend, and track redemptions live. Unused codes auto-expire the moment the linked competition closes.
        </p>
      </div>

      {/* Create batch */}
      <form
        onSubmit={(e) => submit(e, { force: false })}
        className="bg-surface border border-pd rounded-2xl p-5 mb-6"
        data-testid="admin-partner-batch-create"
      >
        <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest mb-3">Generate new batch</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr_0.6fr_1.2fr_auto] gap-3 items-end">
          <Field label="Base word">
            <input
              required value={form.base_word}
              onChange={(e) => { setForm((f) => ({ ...f, base_word: e.target.value.toUpperCase() })); if (collision) setCollision(null); }}
              placeholder="OCTOBER"
              className="w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 font-mono text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none"
              data-testid="partner-batch-create-base"
            />
          </Field>
          <Field label="Partnership label">
            <input
              required value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="October Launch Event"
              className="w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none"
              data-testid="partner-batch-create-label"
            />
          </Field>
          <Field label="Quantity">
            <input
              required type="number" min={1} max={10000}
              value={form.quantity}
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
              className="w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none"
              data-testid="partner-batch-create-qty"
            />
          </Field>
          <Field label="Grants entry into">
            <select
              required value={form.competition_id}
              onChange={(e) => setForm((f) => ({ ...f, competition_id: e.target.value }))}
              className="w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm text-[color:var(--pd-text)] focus:border-[color:var(--pd-accent-yellow)] outline-none"
              data-testid="partner-batch-create-comp"
            >
              <option value="" disabled>Select…</option>
              {liveComps.map((c) => (
                <option key={c.id} value={c.id}>{c.title} ({c.status})</option>
              ))}
            </select>
          </Field>
          <button
            type="submit" disabled={creating}
            className="h-11 inline-flex items-center justify-center gap-2 bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 rounded-xl font-medium disabled:opacity-60"
            data-testid="partner-batch-create-submit"
          >
            {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            Generate
          </button>
        </div>

        {collision ? (
          <div
            className="mt-4 rounded-xl border border-[color:var(--pd-gold)]/60 bg-[color:var(--pd-gold)]/10 p-4 text-sm text-[color:var(--pd-text)]"
            data-testid="partner-batch-collision"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-[color:var(--pd-gold)] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)] mb-1">Collision</div>
                <div>{collision.message}</div>
                {collision.example_existing?.length ? (
                  <div className="mt-2 text-xs text-muted-sage">
                    Examples: <span className="font-mono text-[color:var(--pd-text)]">{collision.example_existing.join(', ')}</span>
                  </div>
                ) : null}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => submit(null, { force: true, start_at: collision.suggested_start_at })}
                    className="inline-flex items-center gap-1.5 text-xs bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-3 py-2 rounded-lg font-medium"
                    data-testid="partner-batch-collision-confirm"
                  >
                    Start from {collision.suggested_start_at} instead
                  </button>
                  <button
                    type="button"
                    onClick={() => setCollision(null)}
                    className="text-xs border border-pd px-3 py-2 rounded-lg text-muted-sage hover:text-[color:var(--pd-text)]"
                    data-testid="partner-batch-collision-cancel"
                  >
                    Change base word
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </form>

      {/* List */}
      <div className="bg-surface border border-pd rounded-2xl overflow-hidden" data-testid="admin-partner-batch-list">
        {loading ? (
          <div className="p-6 text-sm text-muted-sage">Loading batches…</div>
        ) : batches.length === 0 ? (
          <div className="p-6 text-sm text-muted-sage" data-testid="admin-partner-batch-empty">
            No batches yet. Generate one above to start distributing codes to a partner.
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--pd-border)]">
            {batches.map((b) => <BatchRow key={b.id} row={b} />)}
          </ul>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1 block">{label}</span>
    {children}
  </label>
);

const BatchRow = ({ row }) => {
  const c = row.counts || {};
  const compDone = ['SELECTING_WINNER', 'DRAWN'].includes((row.competition_status || '').toUpperCase());
  return (
    <li className="p-4 md:p-5" data-testid="admin-partner-batch-row">
      <Link to={`/admin/partner-codes/${row.id}`} className="flex flex-wrap items-center gap-3 hover:bg-[color:var(--pd-bg-2)]/40 -m-2 p-2 rounded-xl transition-colors">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-base text-[color:var(--pd-accent-yellow)] font-semibold" data-testid="partner-batch-cell-base">
              {row.base_word}1 … {row.base_word}{row.end_at}
            </span>
            <StatusBadge status={row.status} />
            {compDone ? <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-[color:var(--pd-danger)]/15 text-[color:var(--pd-danger)]">Comp closed</span> : null}
          </div>
          <div className="text-sm text-[color:var(--pd-text)] mt-1">{row.label}</div>
          <div className="text-xs text-muted-sage mt-0.5">
            {row.quantity} code{row.quantity === 1 ? '' : 's'} &middot; Grants entry into <span className="text-[color:var(--pd-text)]">{row.competition_title || '—'}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono text-muted-sage">
            <span><span className="text-[color:var(--pd-accent-yellow)]">{c.redeemed ?? 0}</span>/{c.total ?? row.quantity} redeemed</span>
            <span>{c.unused ?? 0} unused</span>
            <span>{c.expired ?? 0} expired</span>
            {c.voided ? <span>{c.voided} voided</span> : null}
            {c.sent ? <span>{c.sent} emailed</span> : null}
            {c.send_failed ? <span className="text-[color:var(--pd-danger)]">{c.send_failed} send failed</span> : null}
          </div>
        </div>
        <ChevronRight size={16} className="text-muted-sage shrink-0" />
      </Link>
    </li>
  );
};

const StatusBadge = ({ status }) => {
  const s = (status || 'active').toLowerCase();
  const cls = s === 'active'
    ? 'bg-[color:var(--pd-accent-yellow)]/15 text-[color:var(--pd-accent-yellow)]'
    : 'bg-[color:var(--pd-border)]/40 text-muted-sage';
  return (
    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${cls}`} data-testid="partner-batch-status">
      {s === 'voided_remaining' ? 'Voided remaining' : s}
    </span>
  );
};

export default AdminPartnerCodes;
